/**
 * Фактура VAT із кошика — поведінка блока `snippets/wxm-invoice-request.liquid`.
 *
 * Що робить:
 *   • тримає стан у АТРИБУТАХ КОШИКА (`Faktura VAT`, `NIP`, `Nazwa firmy`,
 *     `Adres firmy`) — вони їдуть у замовлення, звідки їх читає Office;
 *   • перевіряє контрольну суму NIP (ваги 6,5,7,2,3,4,5,6,7);
 *   • не пускає на оплату, поки галочка стоїть, а NIP хибний;
 *   • ховає кнопки швидкої оплати, поки замовляють на фірму.
 *
 * ⛔ Запити йдуть через XMLHttpRequest, а НЕ через fetch. `wxm-kaucja-tandem.js`
 * підмінює window.fetch і на кожен /cart/update.js запускає перерахунок застави
 * й термобокса + глушить кнопку «Оформити» (`setBusy`). Наш запис міняє лише
 * атрибути, жодної позиції не чіпає — переганяти через нього весь перерахунок на
 * кожну набрану цифру було б і марно, і небезпечно (сіра кнопка = злив кошика,
 * див. інцидент 24.07.2026).
 *
 * ⛔ Слухачі — делеговані на document. Кошик-шухляда Dawn перемальовує свій HTML
 * цілком при кожній зміні кількості, тож будь-який слухач на самому полі загубився б.
 */
(function () {
  'use strict';

  var ATTR_ON = 'Faktura VAT';
  var ATTR_NIP = 'NIP';
  var ATTR_COMPANY = 'Nazwa firmy';
  var ATTR_ADDRESS = 'Adres firmy';
  var ON_VALUE = 'tak';
  var HTML_CLASS = 'wxm-inv-on';
  var SAVE_DELAY_MS = 600;
  var SUBMIT_WAIT_MS = 3000;

  var uk = (document.documentElement.getAttribute('lang') || '').toLowerCase().indexOf('uk') === 0;
  var TEXT = {
    badNip: uk
      ? 'Введіть правильний NIP (10 цифр) — без нього фактуру виставити не можна.'
      : 'Podaj poprawny NIP (10 cyfr) — bez niego nie wystawimy faktury.',
  };

  var saveTimer = null;
  var saving = false;
  var saveAgain = false;
  var dirty = false; // є незбережені зміни
  var resubmitting = false;
  var lastCheckoutButton = null;

  // ── NIP ────────────────────────────────────────────────────────────────────

  /** «PL 123-456-32-18» → «1234563218». */
  function normNip(value) {
    return String(value || '')
      .toUpperCase()
      .replace(/^PL/, '')
      .replace(/[^0-9]/g, '');
  }

  /** Контрольна сума NIP. Залишок 10 — номер недійсний (так у польському стандарті). */
  function validNip(value) {
    var d = normNip(value);
    if (d.length !== 10) return false;
    // «0000000000» формально проходить контрольну суму — відсікаємо однакові цифри.
    if (/^(\d)\1{9}$/.test(d)) return false;
    var weights = [6, 5, 7, 2, 3, 4, 5, 6, 7];
    var sum = 0;
    for (var i = 0; i < 9; i++) sum += weights[i] * Number(d.charAt(i));
    var control = sum % 11;
    if (control === 10) return false;
    return control === Number(d.charAt(9));
  }

  // ── Стан ───────────────────────────────────────────────────────────────────

  function blocks() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-wxm-invoice]'));
  }

  function field(block, name) {
    return block.querySelector('[data-wxm-inv-' + name + ']');
  }

  /**
   * Єдине джерело правди — цей об'єкт, а не поля на сторінці.
   *
   * ⛔ Блоки РІЗНІ: у шухляді лише NIP, на сторінці кошика ще назва й адреса
   * (інакше розкрита форма стискала список товарів на телефоні до нуля). Якби
   * стан читався з «першого блока», збереження з шухляди затерло б назву
   * фірми, набрану на сторінці, порожнім рядком.
   */
  var state = { on: false, nip: '', company: '', address: '' };

  /** Початковий стан із розмітки: беремо кожне поле з того блока, де воно є. */
  function readFromDom() {
    var found = false;
    blocks().forEach(function (block) {
      var on = field(block, 'on');
      if (on) {
        state.on = on.checked;
        found = true;
      }
      ['nip', 'company', 'address'].forEach(function (name) {
        var input = field(block, name);
        if (input && input.value) state[name] = input.value;
      });
    });
    return found;
  }

  /** Розкласти стан по ВСІХ блоках (шухляда + сторінка кошика живуть одночасно). */
  function paint() {
    document.documentElement.classList.toggle(HTML_CLASS, state.on);
    blocks().forEach(function (block) {
      var on = field(block, 'on');
      if (on) on.checked = state.on;
      block.classList.toggle('wxm-inv--on', state.on);

      var fields = block.querySelector('[data-wxm-inv-fields]');
      if (fields) fields.hidden = !state.on;

      [['nip', state.nip], ['company', state.company], ['address', state.address]].forEach(function (pair) {
        var input = field(block, pair[0]);
        // Поле, у якому людина зараз друкує, не чіпаємо — інакше зіб'ємо каретку.
        if (input && input !== document.activeElement && input.value !== pair[1]) input.value = pair[1];
      });

      // Червоне поле і напис — лише коли номер уже дописаний (10 цифр) і не
      // сходиться контрольна сума. На першій цифрі людину не сваримо; порожнє
      // поле ловить страж оформлення нижче.
      var bad = state.on && normNip(state.nip).length >= 10 && !validNip(state.nip);
      var nipInput = field(block, 'nip');
      if (nipInput) nipInput.classList.toggle('wxm-inv__input--bad', bad);
      var error = block.querySelector('[data-wxm-inv-error]');
      if (error) {
        error.hidden = !bad;
        error.textContent = bad ? TEXT.badNip : '';
      }
    });
  }

  function showError(text) {
    blocks().forEach(function (block) {
      var error = block.querySelector('[data-wxm-inv-error]');
      if (!error) return;
      error.hidden = false;
      error.textContent = text;
    });
  }

  // ── Збереження в кошик ─────────────────────────────────────────────────────

  function postAttributes(done) {
    var body = { attributes: {} };
    body.attributes[ATTR_ON] = state.on ? ON_VALUE : '';
    body.attributes[ATTR_NIP] = state.on ? normNip(state.nip) : '';
    body.attributes[ATTR_COMPANY] = state.on ? String(state.company || '').trim() : '';
    body.attributes[ATTR_ADDRESS] = state.on ? String(state.address || '').trim() : '';

    var xhr = new XMLHttpRequest();
    xhr.open('POST', '/cart/update.js', true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.timeout = 8000;
    xhr.onload = function () {
      done(xhr.status >= 200 && xhr.status < 300);
    };
    xhr.onerror = function () {
      done(false);
    };
    xhr.ontimeout = function () {
      done(false);
    };
    xhr.send(JSON.stringify(body));
  }

  function save(done) {
    if (saving) {
      saveAgain = true;
      if (done) done(true); // не тримаємо людину: збереження вже в дорозі
      return;
    }
    saving = true;
    postAttributes(function (ok) {
      saving = false;
      if (ok) dirty = false;
      if (saveAgain) {
        saveAgain = false;
        save();
      }
      if (done) done(ok);
    });
  }

  function scheduleSave() {
    dirty = true;
    clearTimeout(saveTimer);
    saveTimer = setTimeout(function () {
      save();
    }, SAVE_DELAY_MS);
  }

  function flush(done) {
    clearTimeout(saveTimer);
    if (!dirty && !saving) {
      done(true);
      return;
    }
    var finished = false;
    var guard = setTimeout(function () {
      // ⚠️ ЗАПОБІЖНИК: мережа мовчить — не тримаємо клієнта в кошику.
      // Краще замовлення без мітки (додамо фактуру руками), ніж втрачене замовлення.
      if (!finished) {
        finished = true;
        done(false);
      }
    }, SUBMIT_WAIT_MS);
    save(function (ok) {
      if (finished) return;
      finished = true;
      clearTimeout(guard);
      done(ok);
    });
  }

  // ── Події полів ────────────────────────────────────────────────────────────

  document.addEventListener('change', function (e) {
    var target = e.target;
    if (!target || !target.closest) return;
    if (!target.matches('[data-wxm-inv-on]')) return;
    state.on = !!target.checked;
    paint();
    clearTimeout(saveTimer);
    dirty = true;
    save();
  });

  document.addEventListener('input', function (e) {
    var target = e.target;
    if (!target || !target.closest) return;
    if (!target.matches('[data-wxm-inv-nip], [data-wxm-inv-company], [data-wxm-inv-address]')) return;
    if (target.hasAttribute('data-wxm-inv-nip')) state.nip = target.value;
    else if (target.hasAttribute('data-wxm-inv-company')) state.company = target.value;
    else state.address = target.value;
    paint();
    scheduleSave();
  });

  // Пішли з поля — зберігаємо одразу, не чекаючи паузи.
  document.addEventListener(
    'focusout',
    function (e) {
      var target = e.target;
      if (!target || !target.matches) return;
      if (!target.matches('[data-wxm-inv-nip], [data-wxm-inv-company], [data-wxm-inv-address]')) return;
      if (dirty) {
        clearTimeout(saveTimer);
        save();
      }
    },
    true
  );

  // ── Страж оформлення ───────────────────────────────────────────────────────

  document.addEventListener(
    'click',
    function (e) {
      var target = e.target;
      if (!target || !target.closest) return;
      var button = target.closest('[name="checkout"]');
      if (button) lastCheckoutButton = button;
    },
    true
  );

  /** Чи можна пускати на оплату. Хибний NIP при поставленій галочці — ні. */
  function blockedReason() {
    if (!state.on) return null;
    if (validNip(state.nip)) return null;
    return TEXT.badNip;
  }

  function focusNip() {
    // Перше видиме поле NIP (на сторінці /cart їх два — шухляда і підвал).
    var inputs = document.querySelectorAll('[data-wxm-inv-nip]');
    for (var i = 0; i < inputs.length; i++) {
      var input = inputs[i];
      if (input.offsetParent !== null) {
        try {
          input.scrollIntoView({ block: 'center', behavior: 'smooth' });
        } catch (err) {
          input.scrollIntoView();
        }
        input.focus();
        return;
      }
    }
  }

  // Головний рубіж — саме `submit`, а не клік: кнопок оформлення три (смужка
  // зверху шухляди, підвал шухляди, сторінка кошика), а `wixmart-checkout-guard`
  // ще й сам дотискає форму через requestSubmit() — кліку там нема взагалі.
  document.addEventListener(
    'submit',
    function (e) {
      var form = e.target;
      if (!form || !form.matches) return;
      if (form.id !== 'CartDrawer-Form' && form.id !== 'cart') return;
      if (resubmitting) return;

      var submitter = e.submitter || lastCheckoutButton;
      // Форму кошика відправляє ЛИШЕ кнопка оформлення; якщо submitter невідомий,
      // не вгадуємо — пропускаємо, щоб не заблокувати щось стороннє.
      if (submitter && submitter.getAttribute('name') !== 'checkout') return;

      var reason = blockedReason();
      if (reason) {
        e.preventDefault();
        e.stopPropagation();
        showError(reason);
        focusNip();
        return;
      }

      if (!dirty && !saving) return; // усе збережено — не заважаємо

      e.preventDefault();
      e.stopPropagation();
      flush(function () {
        resubmitting = true;
        try {
          if (submitter && typeof form.requestSubmit === 'function') form.requestSubmit(submitter);
          else if (submitter) submitter.click();
          else form.submit();
        } finally {
          setTimeout(function () {
            resubmitting = false;
          }, 1500);
        }
      });
    },
    true
  );

  // ── Початковий стан ────────────────────────────────────────────────────────

  function init() {
    if (!readFromDom()) {
      // Кошик порожній — блока нема, стан ні до чого.
      document.documentElement.classList.remove(HTML_CLASS);
      return;
    }
    paint();

    // ⛔ Галочку могли натиснути, поки цей скрипт ще вантажився (повільний
    // зв'язок): подію ловити було нікому, і в кошику не збереглося нічого.
    // Порівнюємо з тим, що сервер намалював, і дозберігаємо мовчки.
    var saved = false;
    blocks().forEach(function (block) {
      if (block.getAttribute('data-wxm-inv-saved')) saved = true;
    });
    if (state.on !== saved) {
      dirty = true;
      save();
    }
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);

  // Кошик перемальовано (зміна кількості, застава, термобокс) — повертаємо наш
  // стан у свіжу розмітку. Саме стан із пам'яті, а не з розмітки: у ній може
  // бути ще не збережене значення, а в пам'яті — те, що людина набрала.
  document.addEventListener('cart:updated', function () {
    setTimeout(function () {
      if (blocks().length) paint();
      else document.documentElement.classList.remove(HTML_CLASS);
    }, 0);
  });
})();
